/**
 * Preview perangkat: Desktop / Tablet / HP — frame visual saja.
 * JANGAN patch window.innerWidth (merusak Vuetify → layar blank).
 * Layout mobile di dalam frame memakai class html.jgi-device-hp + CSS bridge.
 */
export type DevicePreviewMode = 'desktop' | 'tablet' | 'hp';

export const DEVICE_PRESETS: Record<
  DevicePreviewMode,
  { width: number | null; labelKey: string; icon: string; hintKey: string }
> = {
  desktop: {
    width: null,
    labelKey: 'device.desktop',
    icon: 'mdi-monitor',
    hintKey: 'device.desktopHint',
  },
  tablet: {
    width: 768,
    labelKey: 'device.tablet',
    icon: 'mdi-tablet',
    hintKey: 'device.tabletHint',
  },
  hp: {
    width: 390,
    labelKey: 'device.hp',
    icon: 'mdi-cellphone',
    hintKey: 'device.hpHint',
  },
};

const STORAGE_KEY = 'jgi_device_preview';
const HEAL_KEY = 'jgi_device_preview_heal_v13';
const SAFE_VIEWPORT = 'width=device-width, initial-scale=1';
const CHANGE_EVENT = 'jgi-device-preview-change';

const FRAME_ATTACH = '.jgi-device-app';

export function getRealViewportWidth(): number {
  if (!import.meta.client) return 1024;
  return Math.round(window.innerWidth) || 1024;
}

function readRealHeight(): number {
  if (!import.meta.client) return 800;
  return Math.round(window.innerHeight) || 800;
}

export function restoreSafeViewport() {
  if (!import.meta.client) return;
  const metas = Array.from(
    document.querySelectorAll('meta[name="viewport"]')
  ) as HTMLMetaElement[];
  if (!metas.length) {
    const meta = document.createElement('meta');
    meta.setAttribute('name', 'viewport');
    meta.setAttribute('content', SAFE_VIEWPORT);
    document.head.appendChild(meta);
    return;
  }
  for (const meta of metas) {
    meta.setAttribute('content', SAFE_VIEWPORT);
  }
}

function clearDeviceClasses() {
  const root = document.documentElement;
  root.classList.remove('jgi-device-desktop', 'jgi-device-tablet', 'jgi-device-hp');
  delete root.dataset.devicePreview;
  root.style.removeProperty('--jgi-device-w');
}

function dismissPreviewOverlays() {
  if (!import.meta.client) return;
  // Jangan el.remove() node Vue — merusak Teleport & bisa blankkan frame
  try {
    window.dispatchEvent(new CustomEvent('jgi-close-overlays'));
    document.dispatchEvent(
      new KeyboardEvent('keydown', { key: 'Escape', code: 'Escape', bubbles: true })
    );
  } catch {
    /* ignore */
  }
}

function syncVuetifyOverlayDefaults(framed: boolean) {
  try {
    const nuxtApp = tryUseNuxtApp();
    const vuetify = nuxtApp?.$vuetify as {
      defaults?: { value?: Record<string, Record<string, unknown>> };
    } | undefined;
    const defaults = vuetify?.defaults?.value;
    if (!defaults) return;

    for (const key of ['VMenu', 'VDialog']) {
      const prev = { ...(defaults[key] || {}) };
      if (framed) {
        defaults[key] = { ...prev, attach: FRAME_ATTACH };
      } else {
        const next = { ...prev };
        delete next.attach;
        defaults[key] = next;
      }
    }
    for (const key of ['VSelect', 'VAutocomplete', 'VCombobox']) {
      const prev = { ...(defaults[key] || {}) };
      const prevMenu = { ...((prev.menuProps as Record<string, unknown>) || {}) };
      if (framed) {
        defaults[key] = {
          ...prev,
          menuProps: { ...prevMenu, attach: FRAME_ATTACH, maxHeight: 280 },
        };
      } else {
        const nextMenu = { ...prevMenu };
        delete nextMenu.attach;
        delete nextMenu.maxHeight;
        defaults[key] = { ...prev, menuProps: nextMenu };
      }
    }
  } catch {
    /* ignore */
  }
}

function centerDeviceFrame() {
  if (!import.meta.client) return;
  const stage = document.querySelector('.jgi-device-stage') as HTMLElement | null;
  if (!stage) return;
  try {
    stage.scrollIntoView({ block: 'nearest', inline: 'center', behavior: 'smooth' });
  } catch {
    /* ignore */
  }
  window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
}

/** Tinggi/lebar frame dalam px nyata (tidak bergantung % parent). */
function forceFrameBox(framed: boolean, mode: DevicePreviewMode) {
  if (!import.meta.client) return;
  const stage = document.querySelector('.jgi-device-stage') as HTMLElement | null;
  const app = document.querySelector('.jgi-device-app') as HTMLElement | null;

  if (!framed) {
    for (const el of [stage, app]) {
      if (!el) continue;
      el.style.removeProperty('height');
      el.style.removeProperty('min-height');
      el.style.removeProperty('max-height');
      el.style.removeProperty('width');
      el.style.removeProperty('background');
      el.style.removeProperty('display');
      el.style.removeProperty('flex-direction');
      el.style.removeProperty('overflow');
    }
    return;
  }

  const w = mode === 'hp' ? 390 : 768;
  const hPx = Math.max(560, readRealHeight() - 80);
  const widthCss = `min(${w}px, calc(100vw - 24px))`;

  for (const el of [stage, app]) {
    if (!el) continue;
    if (el === stage) {
      el.style.setProperty('width', widthCss, 'important');
      el.style.setProperty('margin-left', 'auto', 'important');
      el.style.setProperty('margin-right', 'auto', 'important');
    } else {
      el.style.setProperty('width', '100%', 'important');
    }
    el.style.setProperty('height', `${hPx}px`, 'important');
    el.style.setProperty('min-height', `${hPx}px`, 'important');
    el.style.setProperty('max-height', `${hPx}px`, 'important');
  }
  if (app) {
    app.style.setProperty('background', '#e8eef4', 'important');
    app.style.setProperty('display', 'flex', 'important');
    app.style.setProperty('flex-direction', 'column', 'important');
    app.style.setProperty('overflow', 'hidden', 'important');
    app.style.setProperty('border-radius', mode === 'hp' ? '36px' : '28px', 'important');
  }
}

function applyDom(mode: DevicePreviewMode) {
  if (!import.meta.client) return;
  restoreSafeViewport();

  const root = document.documentElement;
  root.classList.remove('jgi-device-desktop', 'jgi-device-tablet', 'jgi-device-hp');
  root.classList.add(`jgi-device-${mode}`);
  root.dataset.devicePreview = mode;

  const w = DEVICE_PRESETS[mode].width;
  if (w) root.style.setProperty('--jgi-device-w', `${w}px`);
  else root.style.removeProperty('--jgi-device-w');

  const framed = mode !== 'desktop';
  syncVuetifyOverlayDefaults(framed);
  forceFrameBox(framed, mode);

  // Jangan dispatch resize palsu — biarkan Vuetify pakai lebar desktop asli
  requestAnimationFrame(() => {
    forceFrameBox(framed, mode);
    if (framed) centerDeviceFrame();
    setTimeout(() => forceFrameBox(framed, mode), 100);
    setTimeout(() => forceFrameBox(framed, mode), 300);
  });
}

function readStored(): DevicePreviewMode {
  if (!import.meta.client) return 'desktop';
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'tablet' || v === 'hp' || v === 'desktop') return v;
  } catch {
    /* ignore */
  }
  return 'desktop';
}

function healStuckPreviewOnce(): DevicePreviewMode {
  try {
    if (localStorage.getItem(HEAL_KEY) === '1') return readStored();
    localStorage.setItem(STORAGE_KEY, 'desktop');
    localStorage.setItem(HEAL_KEY, '1');
  } catch {
    /* ignore */
  }
  return 'desktop';
}

let resizeBound = false;

export function useDevicePreview() {
  const mode = useState<DevicePreviewMode>('jgi-device-preview', () => 'desktop');
  const ready = useState<boolean>('jgi-device-preview-ready', () => false);
  const { t } = useI18n();

  const preset = computed(() => DEVICE_PRESETS[mode.value]);
  const isFramed = computed(() => mode.value !== 'desktop');
  const overlayAttach = computed(() => (isFramed.value ? FRAME_ATTACH : undefined));
  const widthLabel = computed(() => {
    const w = DEVICE_PRESETS[mode.value].width;
    return w ? `${w}px` : t('device.fullWidth');
  });

  function setMode(next: DevicePreviewMode) {
    window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: next }));
    dismissPreviewOverlays();

    const apply = () => {
      mode.value = next;
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        /* ignore */
      }
      applyDom(next);
      nextTick(() => {
        applyDom(next);
        if (next !== 'desktop') centerDeviceFrame();
      });
    };

    setTimeout(apply, 120);
  }

  function load() {
    if (!import.meta.client) return;
    restoreSafeViewport();
    clearDeviceClasses();

    const stored = healStuckPreviewOnce();
    mode.value = stored;
    applyDom(stored);
    ready.value = true;

    setTimeout(() => restoreSafeViewport(), 0);
    setTimeout(() => restoreSafeViewport(), 300);
    setTimeout(() => applyDom(mode.value), 50);
    setTimeout(() => applyDom(mode.value), 250);

    if (!resizeBound) {
      resizeBound = true;
      window.addEventListener('resize', () => {
        if (mode.value !== 'desktop') forceFrameBox(true, mode.value);
      });
    }
  }

  // Pastikan class html selalu sinkron dengan mode Vue (HP CSS bridge)
  if (import.meta.client) {
    watch(
      mode,
      (m) => {
        if (!ready.value) return;
        applyDom(m);
      },
      { flush: 'post' }
    );
  }

  const options = computed(() =>
    (['desktop', 'tablet', 'hp'] as DevicePreviewMode[]).map((id) => ({
      id,
      icon: DEVICE_PRESETS[id].icon,
      title: t(DEVICE_PRESETS[id].labelKey),
      hint: t(DEVICE_PRESETS[id].hintKey),
    }))
  );

  return {
    mode,
    preset,
    isFramed,
    overlayAttach,
    widthLabel,
    options,
    ready,
    setMode,
    load,
    restoreSafeViewport,
    getRealViewportWidth,
    CHANGE_EVENT,
  };
}
